import { useEffect, useState, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatDateTime } from "@/lib/dateFormat";
import {
  Database,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Server,
  BookOpen,
  Pencil,
  Loader2,
  ShieldCheck,
  MoreHorizontal,
  Settings,
  Link2,
  Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import { useSortableData, type SortableColumn } from "@/hooks/useSortableData";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { ObjectGroupAccessDialog } from "@/components/portal/groups/ObjectGroupAccessDialog";
import { useGroupAccessDeepLink } from "@/components/portal/groups/useGroupAccessDeepLink";
import { BulkGroupAccessDialog } from "@/components/portal/groups/BulkGroupAccessDialog";
import { useObjectPermissions } from "@/hooks/useObjectPermissions";
import { PortalApiError } from "@/lib/portalApi";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { groupsApi } from "@/services/api/groups";
import {
  sqlRagApi,
  pollUntilComplete,
  type DataSource,
} from "@/services/api/sqlRag";
import { DataSourceSheet } from "@/components/portal/knowledgebase-sql/DataSourceSheet";
import { DataSourcePreferencesDialog } from "@/components/portal/knowledgebase-sql/DataSourcePreferencesDialog";
import { BrowseSchemaSheet } from "@/components/portal/knowledgebase-sql/BrowseSchemaSheet";
import { kbSqlListIsComplete } from "@/components/portal/knowledgebase-sql/kbSqlListIsComplete";

export const Route = createFileRoute("/portal/knowledgebase-sql/data-sources")({
  component: DataSourcesList,
  validateSearch: (
    search: Record<string, unknown>,
  ): { groupAccess?: string; groupAccessName?: string } => ({
    groupAccess:
      typeof search.groupAccess === "string" && search.groupAccess.length > 0
        ? search.groupAccess
        : undefined,
    groupAccessName:
      typeof search.groupAccessName === "string" && search.groupAccessName.length > 0
        ? search.groupAccessName
        : undefined,
  }),
});

const DATABASE_TYPE_LABELS: Record<string, string> = {
  sqlserver: "SQL Server",
  postgresql: "PostgreSQL",
  mysql: "MySQL",
};

type DataSourceRecord = DataSource & Record<string, unknown>;

const sortColumns: SortableColumn<DataSourceRecord>[] = [
  { key: "name", getValue: (r) => r.name ?? "" },
  { key: "database_type", getValue: (r) => r.database_type ?? "" },
  { key: "server_host", getValue: (r) => r.server_host ?? "" },
  { key: "is_validated", getValue: (r) => (r.is_validated ? 1 : 0) },
  { key: "query_count", getValue: (r) => r.query_count ?? 0 },
  { key: "last_used_at", getValue: (r) => r.last_used_at ?? "" },
  { key: "last_synced_at", getValue: (r) => r.last_synced_at ?? "" },
];

const MAX_VISIBLE_BADGES = 2;

function DataSourcesList() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin, bypassesGroups } = usePermissions();
  const canManageGroups = can("edit", "groups") || isAdmin;
  // Data Sources serves both subscriptions (claude-connect A1): a Connect-only
  // org holds `claude-connect`, not `kb-sql`.
  const canCreateDataSources =
    can("create", "kb-sql") || can("create", "claude-connect");
  const canEditDataSources =
    can("edit", "kb-sql") || can("edit", "claude-connect");
  const canDeleteDataSources =
    can("delete", "kb-sql") || can("delete", "claude-connect");

  const {
    perms: sqlRagPerms,
    canEdit: objCanEdit,
    canDelete: objCanDelete,
  } = useObjectPermissions(
    token ? () => sqlRagApi.getMyPermissions(token) : null,
    [token, selectedOrganisation?.organisationId],
  );

  // Data state
  const [loading, setLoading] = useState(true);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);
  // A failed load leaves `dataSources` empty, which combined with `listIsComplete: true`
  // would read as "this org has no data sources" and report a false "Object not found"
  // for the deep link. The page itself only toasts, so nothing else recorded this.
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sheet / dialog state
  const [showSheet, setShowSheet] = useState(false);
  const [editingDataSource, setEditingDataSource] = useState<DataSource | null>(null);
  // How Claude uses a source (dp #2558): org admin and above, every source.
  const [preferencesTarget, setPreferencesTarget] = useState<DataSource | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRemovePermanentlyDialog, setShowRemovePermanentlyDialog] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [browseSchemaTarget, setBrowseSchemaTarget] = useState<{
    id: string;
    name: string;
    connectorBacked: boolean;
  } | null>(null);

  // Group access state
  const [groupAccessTarget, setGroupAccessTarget] = useState<{ id: string; name: string } | null>(null);

  const isConnectorBacked = (ds: DataSource) => ds.source === "b2c-connector";
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGroupAccessOpen, setBulkGroupAccessOpen] = useState(false);
  const [dsPermissions, setDsPermissions] = useState<
    Map<string, { group_name: string; role: string }[]>
  >(new Map());

  // Sorting
  const { sortedData, requestSort, getSortDirection } = useSortableData(
    dataSources as DataSourceRecord[],
    {
      initialSort: { key: "name", direction: "asc" },
      columns: sortColumns,
    },
  );

  // Client-side pagination
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paginatedData,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData<DataSourceRecord>({
    data: sortedData,
    itemsPerPage: 25,
    searchFields: ["name", "description", "server_host", "database_name"],
    searchTerm,
  });

  const displayData = paginatedData as unknown as DataSource[];

  // Deep link from the group Object Permissions tab: ?groupAccess=<data source id>
  const { groupAccess, groupAccessName } = Route.useSearch();
  useGroupAccessDeepLink({
    groupAccess,
    groupAccessName,
    // `listDataSources(token, false)` returns every ACTIVE data source in one call — the
    // pagination on this page is purely client-side over `dataSources`. Active is the
    // right population because DP's delete is a deactivate (`sql_rag.py` —
    // "Delete (deactivate) a data source"), so inactive means deleted, which is exactly
    // what the deep link should report as not found.
    // AND the caller bypasses groups FOR `sql_rag`: this list is filtered server-side by
    // the caller's own object permissions unless they bypass, so for a non-bypass caller
    // "complete" still omits objects that exist. Reporting those as absent is a false,
    // unrecoverable not-found.
    // The flag is the `sql_rag` one from `/sql-rag/my-permissions`, NOT the portal-wide
    // `usePermissions().bypassesGroups`: since kb-db-access-lockdown W1 an org_admin
    // still bypasses portal-wide but not for `sql_rag` (Xero KB sources need a grant),
    // so DP drops sources from their list that the portal-wide flag says they'd see.
    listIsComplete: kbSqlListIsComplete(sqlRagPerms),
    // Wait for that flag too: acting before it settles would read "not complete" for a
    // caller who in fact bypasses, opening the dialog on an orphan grant's dead id.
    ready:
      !loading &&
      !loadFailed &&
      sqlRagPerms !== null &&
      !!selectedOrganisation?.organisationId,
    canManageGroups,
    items: dataSources,
    getId: (ds) => ds.id,
    getName: (ds) => ds.name,
    // One ACL (web #1311): every row, connector-backed included, is governed
    // by its own sql_rag grants, so the deep link opens the dialog for all.
    onMatch: (ds) => setGroupAccessTarget(ds),
  });

  // Clear selection on search / page change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, currentPage]);

  // Selection helpers

  // Every row is selectable for bulk Group Access: a connector-backed row is
  // governed by its own sql_rag grants exactly as a custom one (web #1311).
  const selectableOnPage = displayData;
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((ds) => selectedIds.has(ds.id));
  const someOnPageSelected = selectableOnPage.some((ds) => selectedIds.has(ds.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableOnPage.map((ds) => ds.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = displayData
    .filter((ds) => selectedIds.has(ds.id))
    .map((ds) => ({ id: ds.id, name: ds.name }));

  // Fetch data
  useEffect(() => {
    if (token) loadDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrganisation?.organisationId]);

  const loadDataSources = async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const response = await sqlRagApi.listDataSources(token, false);
      setDataSources(response);
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      if (error instanceof PortalApiError && error.isPermissionDenied) {
        setAccessDenied(true);
      } else {
        console.error("Failed to load data sources:", error);
        toast.error("Failed to load data sources");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch group permissions in bulk
  const orgId = selectedOrganisation?.organisationId;
  const fetchDsPermissions = useCallback(async () => {
    // cp answers by-object only for org_owner / org_admin / platform_admin (BYPASS_ROLES,
    // `_require_grant_visibility`); anyone else gets a 403 that trips Platform Health (web #1444).
    if (!token || !orgId || !bypassesGroups) return;
    try {
      const resp = await groupsApi.listObjectPermissionsByObject(token, orgId, {
        service_name: "sql_rag",
        object_type: "data_source",
      });
      const grouped = new Map<string, { group_name: string; role: string }[]>();
      for (const item of resp.items) {
        const existing = grouped.get(item.object_id) ?? [];
        existing.push({ group_name: item.group_name, role: item.role });
        grouped.set(item.object_id, existing);
      }
      setDsPermissions(grouped);
    } catch {
      // Non-critical
    }
  }, [token, orgId, bypassesGroups]);

  useEffect(() => {
    fetchDsPermissions();
  }, [fetchDsPermissions, dataSources]);

  // Handlers
  const handleSyncSchema = async (dataSource: DataSource) => {
    if (!token) return;
    setSyncing(dataSource.id);
    try {
      const enqueueResponse = await sqlRagApi.syncSchema(token, dataSource.id);
      const statusResponse = await pollUntilComplete(
        () => sqlRagApi.getSyncSchemaJobStatus(token, dataSource.id, enqueueResponse.job_id),
        (s) => s.status === "completed" || s.status === "failed",
        2000,
      );
      if (statusResponse.status === "failed") {
        toast.error(statusResponse.error || "Schema sync failed");
        return;
      }
      if (statusResponse.grant_errors && statusResponse.grant_errors > 0) {
        toast.warning(
          `Schema synced: ${statusResponse.tables_synced || 0} tables found, ${statusResponse.grant_errors} with insufficient grants — see Browse Schema`,
        );
      } else {
        toast.success(`Schema synced: ${statusResponse.tables_synced || 0} tables found`);
      }
      await loadDataSources(true);
    } catch (error) {
      console.error("Failed to sync schema:", error);
      toast.error("Failed to sync schema");
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedDataSource || !token) return;
    setDeleting(true);
    try {
      await sqlRagApi.deleteDataSource(token, selectedDataSource.id);
      toast.success("Data source deleted");
      setShowDeleteDialog(false);
      setShowRemovePermanentlyDialog(false);
      setSelectedDataSource(null);
      await loadDataSources(true);
    } catch (error) {
      console.error("Failed to delete data source:", error);
      toast.error("Failed to delete data source");
    } finally {
      setDeleting(false);
    }
  };


  const renderDataSourceMenuItems = (ds: DataSource) => (
    <>
      {canEditDataSources && objCanEdit(ds.id) && !isConnectorBacked(ds) && (
        <DropdownMenuItem
          onClick={() => {
            setEditingDataSource(ds);
            setShowSheet(true);
          }}
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </DropdownMenuItem>
      )}
      {canEditDataSources && objCanEdit(ds.id) && (
        <DropdownMenuItem
          onClick={() => handleSyncSchema(ds)}
          disabled={syncing === ds.id}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${syncing === ds.id ? "animate-spin" : ""}`}
          />
          {syncing === ds.id ? "Syncing..." : "Sync Schema"}
        </DropdownMenuItem>
      )}
      <DropdownMenuItem
        onClick={() =>
          setBrowseSchemaTarget({
            id: ds.id,
            name: ds.name,
            connectorBacked: isConnectorBacked(ds),
          })
        }
        disabled={!ds.is_validated}
      >
        <Database className="h-4 w-4 mr-2" />
        Browse Schema
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() =>
          bypassesGroups
            ? setPreferencesTarget(ds)
            : toast.info(
                "Only organisation owners and admins can change a data source's preferences.",
              )
        }
      >
        <Settings className="h-4 w-4 mr-2" />
        Preferences
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link
          to="/portal/knowledgebase-sql/glossary"
          search={{ dataSource: ds.id }}
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Business Glossary
        </Link>
      </DropdownMenuItem>
      {canManageGroups && (
        <DropdownMenuItem
          onClick={() =>
            setGroupAccessTarget({
              id: ds.id,
              name: ds.name || `${ds.server_host}/${ds.database_name}`,
            })
          }
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          Group Access
        </DropdownMenuItem>
      )}
      {canDeleteDataSources && objCanDelete(ds.id) && !isConnectorBacked(ds) && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setSelectedDataSource(ds);
              setShowDeleteDialog(true);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </>
      )}
      {canDeleteDataSources && objCanDelete(ds.id) && isConnectorBacked(ds) && ds.retired_at_utc !== null && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setSelectedDataSource(ds);
              setShowRemovePermanentlyDialog(true);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove permanently
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const getStatusBadge = (dataSource: DataSource) => {
    if (syncing === dataSource.id) {
      return (
        <Badge variant="warning" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Syncing
        </Badge>
      );
    }
    if (!dataSource.is_active) {
      return (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />
          Inactive
        </Badge>
      );
    }
    if (dataSource.is_validated) {
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          Ready
        </Badge>
      );
    }
    if (dataSource.validation_error) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Not Synced
      </Badge>
    );
  };

  // Origin + retirement badges (plan: connector-to-kb-db-rbac-and-credential-centralisation.md).
  // Custom rows get no badge — the absence IS the signal, matching how getStatusBadge
  // only shows something noteworthy. Connector-backed rows always show "B2C Connector"
  // (plus the underlying connector type, e.g. Xero/MailChimp, when known); a retired one
  // additionally shows "Retired" (D5: a label only, still fully queryable).
  const getSourceBadges = (dataSource: DataSource) => {
    if (dataSource.source !== "b2c-connector") return null;
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        <Badge variant="outline" className="gap-1 text-xs">
          <Link2 className="h-3 w-3" />
          B2C Connector
          {dataSource.connector_type && (
            <>
              <span className="text-muted-foreground">·</span>
              {dataSource.connector_type}
            </>
          )}
        </Badge>
        {dataSource.retired_at_utc && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Archive className="h-3 w-3" />
            Retired
          </Badge>
        )}
      </div>
    );
  };

  // Use shared date formatting (UTC → browser local, en-ZA locale)
  const formatDateLocal = (dateString: string | null) => {
    if (!dateString) return "—";
    return formatDateTime(dateString);
  };

  if (accessDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to access data sources. Ask your administrator to grant you access."
        backTo="/portal/knowledgebase-sql"
        backLabel="Back to Knowledgebase DB"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">Data Sources</h1>
          <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
            Connect to external databases for natural language queries
          </p>
        </div>
        {canCreateDataSources && (
          <Button
            className="rounded-full"
            onClick={() => {
              setEditingDataSource(null);
              setShowSheet(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Data Source
          </Button>
        )}
      </div>

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-4 md:space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search data sources..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={() => loadDataSources()}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && canManageGroups && (
          <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} data source{selectedIds.size !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkGroupAccessOpen(true)}
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Group Access
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : dataSources.length === 0 ? (
          <div className="text-center py-12">
            <Server className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No data sources configured</h3>
            <p className="text-muted-foreground mb-4">
              Connect your first database to enable natural language queries.
            </p>
            {canCreateDataSources && (
              <Button
                onClick={() => {
                  setEditingDataSource(null);
                  setShowSheet(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Data Source
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageGroups && (
                    <TableHead className="w-[40px] px-3">
                      <Checkbox
                        checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                  )}
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("database_type")}
                    onSort={() => requestSort("database_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("server_host")}
                    onSort={() => requestSort("server_host")}
                    className="hidden md:table-cell"
                  >
                    Server
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("is_validated")}
                    onSort={() => requestSort("is_validated")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("query_count")}
                    onSort={() => requestSort("query_count")}
                    className="hidden lg:table-cell"
                  >
                    Queries
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("last_used_at")}
                    onSort={() => requestSort("last_used_at")}
                    className="hidden lg:table-cell"
                  >
                    Last Used
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("last_synced_at")}
                    onSort={() => requestSort("last_synced_at")}
                    className="hidden lg:table-cell"
                  >
                    Last Synced
                  </SortableTableHead>
                  {canManageGroups && (
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="hidden lg:table-cell">
                      Groups
                    </SortableTableHead>
                  )}
                  <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px] sticky right-0 bg-background z-10 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((ds) => (
                  <TableRow key={ds.id}>
                    {canManageGroups && (
                      <TableCell className="px-3">
                        <Checkbox
                          checked={selectedIds.has(ds.id)}
                          onCheckedChange={() => toggleSelect(ds.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-left hover:underline cursor-pointer">
                            <div className="font-medium">{ds.name}</div>
                            {ds.description && (
                              <div className="text-sm text-muted-foreground truncate max-w-xs">
                                {ds.description}
                              </div>
                            )}
                            {getSourceBadges(ds)}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {renderDataSourceMenuItems(ds)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {DATABASE_TYPE_LABELS[ds.database_type] || ds.database_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm">
                        {ds.server_host || "—"}
                        {ds.database_name && (
                          <span className="text-muted-foreground"> / {ds.database_name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(ds)}</TableCell>
                    <TableCell className="hidden lg:table-cell">{ds.query_count.toLocaleString()}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {formatDateLocal(ds.last_used_at)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {formatDateLocal(ds.last_synced_at)}
                    </TableCell>
                    {canManageGroups && (
                      <TableCell className="hidden lg:table-cell">
                        <GroupBadges permissions={dsPermissions.get(ds.id)} />
                      </TableCell>
                    )}
                    <TableCell className="sticky right-0 z-10 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {renderDataSourceMenuItems(ds)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  totalCount={totalItems}
                  pageSize={itemsPerPage}
                  pageSizeOptions={[10, 25, 50, 100]}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={currentPage > 1}
                  canGoNext={currentPage < totalPages}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setItemsPerPage}
                  onFirstPage={() => setCurrentPage(1)}
                  onLastPage={() => setCurrentPage(totalPages)}
                  onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Data Source</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedDataSource?.name}
              &quot;? This will also remove all schema metadata and preferences.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Permanently Confirmation Dialog (retired connector-backed rows only) */}
      <Dialog open={showRemovePermanentlyDialog} onOpenChange={setShowRemovePermanentlyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Permanently</DialogTitle>
            <DialogDescription>
              This retired source will be removed permanently, along with its
              cached schema, its glossary terms, and any insight reports built
              on it. It will reappear automatically if the connector is ever
              reactivated — but those reports will not.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRemovePermanentlyDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Removing..." : "Remove permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Data Source Sheet */}
      <DataSourcePreferencesDialog
        open={preferencesTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPreferencesTarget(null);
        }}
        dataSource={preferencesTarget}
        onSaved={() => void loadDataSources(true)}
      />
      <DataSourceSheet
        open={showSheet}
        onOpenChange={setShowSheet}
        dataSource={editingDataSource}
        organisationId={selectedOrganisation?.organisationId ?? ""}
        onSaved={() => loadDataSources(true)}
      />

      {/* Single Group Access Dialog */}
      {groupAccessTarget && (
        <ObjectGroupAccessDialog
          serviceName="sql_rag"
          objectType="data_source"
          objectId={groupAccessTarget.id}
          objectName={groupAccessTarget.name}
          open={!!groupAccessTarget}
          onOpenChange={(open) => {
            if (!open) {
              setGroupAccessTarget(null);
              fetchDsPermissions();
            }
          }}
        />
      )}

      {/* Browse Schema Sheet */}
      {browseSchemaTarget && (
        <BrowseSchemaSheet
          open={!!browseSchemaTarget}
          onOpenChange={(o) => {
            if (!o) setBrowseSchemaTarget(null);
          }}
          dataSourceId={browseSchemaTarget.id}
          dataSourceName={browseSchemaTarget.name}
          readOnly={browseSchemaTarget.connectorBacked}
        />
      )}

      {/* Bulk Group Access Dialog */}
      <BulkGroupAccessDialog
        serviceName="sql_rag"
        objectType="data_source"
        items={selectedItems}
        open={bulkGroupAccessOpen}
        onOpenChange={setBulkGroupAccessOpen}
        onComplete={() => {
          setSelectedIds(new Set());
          fetchDsPermissions();
        }}
      />
    </div>
  );
}

function GroupBadges({ permissions }: { permissions?: { group_name: string; role: string }[] }) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const visible = permissions.slice(0, MAX_VISIBLE_BADGES);
  const remaining = permissions.length - MAX_VISIBLE_BADGES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-1">
        {visible.map((p, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs cursor-default">
                {p.group_name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{p.role}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs cursor-default">
                +{remaining}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {permissions
                .slice(MAX_VISIBLE_BADGES)
                .map((p) => `${p.group_name} (${p.role})`)
                .join(", ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
